import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { enhanceQuoteDrawingSvgElement } from './enhanceQuoteDrawingSvg';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

/** A4 at 96 CSS dpi – preview and capture use identical pixel dimensions */
export const A4_WIDTH_PX = 794;
export const A4_HEIGHT_PX = 1123;

/** Target max file size for downloaded quote PDFs (e.g. email attachment limits) */
export const PDF_MAX_BYTES = 10 * 1024 * 1024;

const PDF_CAPTURE_SCALE = 2;
const PDF_JPEG_QUALITY_START = 0.9;
const PDF_JPEG_QUALITY_MIN = 0.65;
const PDF_JPEG_QUALITY_STEP = 0.05;

/**
 * Minimal clone CSS – only what html2canvas cannot infer from the live preview.
 * Layout, fonts, borders, and alignment come from quotePdf.css (same as on screen).
 */
const PDF_CLONE_CSS = `
  [data-pdf-page],
  .quote-pdf-page {
    width: ${A4_WIDTH_PX}px !important;
    height: ${A4_HEIGHT_PX}px !important;
    min-height: ${A4_HEIGHT_PX}px !important;
    max-height: ${A4_HEIGHT_PX}px !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
    background: #fff !important;
    margin: 0 !important;
  }
  .quote-pdf-page-inner {
    width: 100% !important;
    height: 100% !important;
    min-height: ${A4_HEIGHT_PX}px !important;
    max-height: ${A4_HEIGHT_PX}px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
  .pdf-export-hide,
  .vmk-remove-btn {
    display: none !important;
  }
  .vmk-drawing-inner svg foreignObject,
  .vmk-drawing-inner svg text[data-dimension-label='true'] {
    opacity: 0 !important;
    visibility: hidden !important;
    pointer-events: none !important;
  }
  .vmk-quote-table tr.vmk-price-row td {
    padding: 0 !important;
    vertical-align: middle !important;
  }
  .vmk-yellow-row td {
    background-color: #ffff00 !important;
  }
  .vmk-price-cell-inner {
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
    width: 100% !important;
    min-height: 28px !important;
    padding: 6px 8px !important;
    line-height: 1.2 !important;
  }
  .vmk-price-cell-inner--label {
    justify-content: center !important;
    text-align: center !important;
  }
  .vmk-price-cell-inner--value {
    justify-content: flex-end !important;
    text-align: right !important;
    padding-right: 8px !important;
  }
  .vmk-totals table td {
    padding: 0 !important;
    vertical-align: middle !important;
    background-color: #ffff00 !important;
  }
  .vmk-totals-cell-inner {
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;
    width: 100% !important;
    min-height: 28px !important;
    padding: 6px 8px !important;
    line-height: 1.2 !important;
    white-space: nowrap !important;
  }
  .vmk-totals-cell-inner--label {
    justify-content: flex-start !important;
    text-align: left !important;
  }
  .vmk-totals-cell-inner--value {
    justify-content: flex-end !important;
    text-align: right !important;
  }
  .vmk-page-header,
  .vmk-page-offer-block,
  .vmk-brand-title,
  .vmk-brand-subtitle,
  .vmk-header-address,
  .vmk-header-address p,
  .vmk-offerte-ref-box,
  .vmk-offerte-ref-box span,
  .vmk-header-date,
  .vmk-intro,
  .vmk-intro p {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10px !important;
    line-height: 1.4 !important;
  }
  .vmk-brand-title {
    font-weight: 700 !important;
  }
  .vmk-dim-label-overlay {
    font-size: 10px !important;
    font-weight: 500 !important;
  }
  .vmk-proposal-benefits-section {
    overflow: visible !important;
    position: relative !important;
    z-index: 2 !important;
    background: #fff !important;
  }
  .vmk-proposal-badge,
  .vmk-cover-badge {
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    left: auto !important;
    z-index: 20 !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-proposal-badge {
    width: 44px !important;
    height: 44px !important;
    background: #179f9f !important;
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .vmk-cover-badge {
    width: 40px !important;
    height: 40px !important;
    background: #179f9f !important;
    color: #fff !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .vmk-proposal-benefit-icon-wrap,
  .vmk-proposal-benefit-icon {
    width: 46px !important;
    height: 46px !important;
    min-width: 46px !important;
    min-height: 46px !important;
    overflow: visible !important;
    flex-shrink: 0 !important;
    display: block !important;
  }
  .vmk-proposal-benefit-icon path,
  .vmk-proposal-benefit-icon circle,
  .vmk-proposal-benefit-icon rect {
    stroke: #179f9f !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-proposal-check-icon {
    width: 18px !important;
    height: 18px !important;
    min-width: 18px !important;
    min-height: 18px !important;
    overflow: visible !important;
    flex-shrink: 0 !important;
    display: block !important;
  }
  .vmk-proposal-check-icon circle,
  .vmk-proposal-check-icon path {
    stroke: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-specs-page {
    overflow: hidden !important;
  }
  .vmk-specs-card {
    background: #eef1f2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-specs-icon-image {
    width: 86px !important;
    height: 86px !important;
    overflow: visible !important;
    flex-shrink: 0 !important;
    display: block !important;
  }
  .vmk-drawing-page,
  .vmk-drawing-page .vmk-proposal-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-drawing-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  .vmk-drawing-page .vmk-template-footer,
  .vmk-drawing-page .vmk-proposal-footer {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-investment-page,
  .vmk-investment-page .vmk-investment-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-investment-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }
  .vmk-investment-hero,
  .vmk-investment-table tfoot td {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-investment-table {
    font-size: 14px !important;
  }
  .vmk-investment-table thead th {
    font-size: 13px !important;
  }
  .vmk-investment-table tfoot td {
    font-size: 14px !important;
  }
  .vmk-investment-included-icon-wrap,
  .vmk-investment-included-icon {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    min-height: 40px !important;
    overflow: visible !important;
    flex-shrink: 0 !important;
    display: block !important;
  }
  .vmk-investment-included-icon path,
  .vmk-investment-included-icon circle,
  .vmk-investment-included-icon rect {
    stroke: #179f9f !important;
  }
  .vmk-warranty-page,
  .vmk-warranty-page .vmk-warranty-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-warranty-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .vmk-warranty-list {
    flex: 1 1 auto !important;
    min-height: 0 !important;
  }
  .vmk-warranty-row {
    flex: 1 1 0 !important;
    min-height: 72px !important;
  }
  .vmk-warranty-icon-wrap,
  .vmk-warranty-row,
  .vmk-warranty-page .vmk-template-footer {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-warranty-icon-wrap {
    width: 44px !important;
    height: 44px !important;
    min-width: 44px !important;
    min-height: 44px !important;
    flex-shrink: 0 !important;
    display: flex !important;
    background: #179f9f !important;
  }
  .vmk-warranty-icon path,
  .vmk-warranty-icon circle,
  .vmk-warranty-icon rect {
    stroke: #fff !important;
  }
  .vmk-process-page,
  .vmk-process-page .vmk-process-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-process-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .vmk-process-timeline {
    flex: 1 1 auto !important;
    min-height: 0 !important;
  }
  .vmk-process-step {
    flex: 1 1 0 !important;
  }
  .vmk-process-timeline::before,
  .vmk-process-step-num,
  .vmk-process-icon-box,
  .vmk-process-page .vmk-template-footer {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-process-icon-box {
    width: 56px !important;
    height: 56px !important;
    min-width: 56px !important;
    min-height: 56px !important;
    flex-shrink: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: #eef1f2 !important;
  }
  .vmk-process-step-icon path,
  .vmk-process-step-icon circle,
  .vmk-process-step-icon rect {
    stroke: #179f9f !important;
  }
  .vmk-sustainability-page,
  .vmk-sustainability-page .vmk-sustainability-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-sustainability-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .vmk-sustainability-photo-wrap {
    flex: 1 1 auto !important;
    min-height: 180px !important;
    overflow: hidden !important;
  }
  .vmk-sustainability-photo {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    display: block !important;
  }
  .vmk-sustainability-card,
  .vmk-sustainability-savings,
  .vmk-sustainability-page .vmk-template-footer {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-sustainability-card-icon path,
  .vmk-sustainability-card-icon circle,
  .vmk-sustainability-card-icon rect {
    stroke: #179f9f !important;
  }
  .vmk-agreement-page,
  .vmk-agreement-page .vmk-agreement-shell {
    width: 794px !important;
    height: 1123px !important;
    min-height: 1123px !important;
    max-height: 1123px !important;
    overflow: hidden !important;
  }
  .vmk-agreement-main {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .vmk-agreement-signature-box {
    height: 168px !important;
    min-height: 168px !important;
    max-height: 168px !important;
    flex: 0 0 auto !important;
  }
  .vmk-agreement-brand-row {
    margin-top: auto !important;
    padding-top: 24px !important;
  }
  .vmk-agreement-page .vmk-template-footer {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .vmk-agreement-qr-image {
    width: 108px !important;
    height: 108px !important;
    display: block !important;
  }
  .vmk-cover-panel {
    align-items: stretch !important;
  }
  .vmk-cover-panel-inner {
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box !important;
    padding: 0 44px 62px !important;
  }
  .vmk-agreement-main {
    align-items: stretch !important;
    width: 100% !important;
  }
  .vmk-agreement-intro,
  .vmk-agreement-fields,
  .vmk-agreement-field,
  .vmk-agreement-field-line {
    width: 100% !important;
    max-width: 100% !important;
    text-align: left !important;
  }
  .vmk-agreement-signature-box {
    width: 100% !important;
    justify-content: flex-start !important;
    padding-left: 12px !important;
    box-sizing: border-box !important;
  }
`;

function stripEmbeddedDrawingLabelsForPdf(root) {
  root.querySelectorAll('.vmk-drawing-inner svg').forEach((svg) => {
    svg.querySelectorAll('foreignObject').forEach((node) => node.remove());
    enhanceQuoteDrawingSvgElement(svg);
    svg.querySelectorAll('text, tspan').forEach((node) => {
      const content = (node.textContent || '').trim().replace(/\s*mm\s*$/i, '');
      if (/^\d+$/.test(content)) {
        node.remove();
      }
    });
  });
}

/** Keep page number badges inside the 794px capture box (html2canvas clips flush-right nodes). */
function ensureTemplateBadgesInBounds(livePage, clonedPage) {
  const pageWidth = livePage.offsetWidth || A4_WIDTH_PX;

  clonedPage.querySelectorAll('.vmk-proposal-badge, .vmk-cover-badge').forEach((clonedBadge) => {
    const selector = clonedBadge.classList.contains('vmk-cover-badge')
      ? '.vmk-cover-badge'
      : '.vmk-proposal-badge';
    const liveBadge = livePage.querySelector(selector);
    if (!liveBadge) return;

    const width = Math.round(liveBadge.getBoundingClientRect().width) || clonedBadge.offsetWidth || 44;
    const height = Math.round(liveBadge.getBoundingClientRect().height) || clonedBadge.offsetHeight || 44;
    const pageRect = livePage.getBoundingClientRect();
    const badgeRect = liveBadge.getBoundingClientRect();
    const top = Math.max(0, Math.round(badgeRect.top - pageRect.top));
    const overflowRight = badgeRect.right - pageRect.right;
    const right = Math.max(0, Math.ceil(overflowRight));

    clonedBadge.style.position = 'absolute';
    clonedBadge.style.top = `${top}px`;
    clonedBadge.style.right = `${right}px`;
    clonedBadge.style.left = 'auto';
    clonedBadge.style.width = `${width}px`;
    clonedBadge.style.height = `${height}px`;
    clonedBadge.style.maxWidth = `${width}px`;
    clonedBadge.style.zIndex = '20';
    clonedBadge.style.overflow = 'visible';
    clonedBadge.style.boxSizing = 'border-box';
    clonedBadge.style.display = 'flex';
    clonedBadge.style.alignItems = 'center';
    clonedBadge.style.justifyContent = 'center';
    clonedBadge.style.background = '#179f9f';
    clonedBadge.style.color = '#fff';
    clonedBadge.style.fontWeight = '700';
    clonedBadge.style.fontSize = clonedBadge.classList.contains('vmk-cover-badge') ? '13px' : '14px';
    clonedBadge.style.webkitPrintColorAdjust = 'exact';
    clonedBadge.style.printColorAdjust = 'exact';
  });

  clonedPage.style.maxWidth = `${pageWidth}px`;
  clonedPage.style.overflow = 'hidden';
}

/** Copy stylesheet links so the clone matches the live preview typography/layout */
function ensureCloneStyles(clonedDoc) {
  const head = clonedDoc.head;
  if (!head) return;

  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    if (link.href && !head.querySelector(`link[href="${link.href}"]`)) {
      head.appendChild(link.cloneNode(true));
    }
  });

  // Include Tailwind / bundled CSS (vmk-page-header uses utility classes)
  document.querySelectorAll('style').forEach((style) => {
    head.appendChild(style.cloneNode(true));
  });
}

/** Keep dimension overlay positions identical to the on-screen preview */
function mirrorOverlayPositions(livePage, clonedPage) {
  const liveOverlays = livePage.querySelectorAll('.vmk-dim-label-overlay');
  const clonedOverlays = clonedPage.querySelectorAll('.vmk-dim-label-overlay');

  liveOverlays.forEach((liveEl, index) => {
    const clonedEl = clonedOverlays[index];
    if (!clonedEl) return;

    const cs = window.getComputedStyle(liveEl);
    clonedEl.style.left = liveEl.style.left;
    clonedEl.style.top = liveEl.style.top;
    clonedEl.style.fontSize = liveEl.style.fontSize || cs.fontSize;
    clonedEl.style.fontWeight = cs.fontWeight;
    clonedEl.style.fontFamily = cs.fontFamily;
    clonedEl.style.color = cs.color;
    clonedEl.style.transform = cs.transform;
    clonedEl.style.whiteSpace = 'nowrap';
    clonedEl.style.lineHeight = cs.lineHeight || '1';
  });
}

function prepareClonedPage(livePage, clonedDoc, clonedPage) {
  ensureCloneStyles(clonedDoc);

  const cloneStyle = clonedDoc.createElement('style');
  cloneStyle.textContent = PDF_CLONE_CSS;
  clonedDoc.head.appendChild(cloneStyle);

  clonedPage.querySelectorAll('.pdf-export-hide, .vmk-remove-btn').forEach((el) => {
    el.style.display = 'none';
  });

  clonedPage.querySelectorAll('.vmk-drawing-inner svg rect').forEach((rect) => {
    const fill = rect.getAttribute('fill');
    if (fill === '#F5F5F5' || fill === '#f5f5f5') {
      rect.setAttribute('fill', '#FFF');
    }
  });

  stripEmbeddedDrawingLabelsForPdf(clonedPage);
  mirrorOverlayPositions(livePage, clonedPage);
  syncFlexCellRowHeights(livePage, clonedPage);
  clonedPage.querySelectorAll('.vmk-proposal-benefit-icon-wrap, .vmk-proposal-benefit-icon').forEach((el) => {
    el.style.width = '46px';
    el.style.height = '46px';
    el.style.minWidth = '46px';
    el.style.minHeight = '46px';
    el.style.overflow = 'visible';
    el.style.flexShrink = '0';
    el.style.display = 'block';
  });
  clonedPage.querySelectorAll('.vmk-proposal-check-icon').forEach((el) => {
    el.style.width = '18px';
    el.style.height = '18px';
    el.style.minWidth = '18px';
    el.style.minHeight = '18px';
    el.style.overflow = 'visible';
    el.style.flexShrink = '0';
    el.style.display = 'block';
  });
  clonedPage.querySelectorAll('.vmk-proposal-logo').forEach((el) => {
    const isSmall = el.classList.contains('vmk-proposal-logo--sm');
    el.style.height = isSmall ? '32px' : '40px';
    el.style.maxHeight = isSmall ? '32px' : '40px';
    el.style.width = 'auto';
    el.style.objectFit = 'contain';
    el.style.objectPosition = 'left center';
    el.style.display = 'block';
  });
  clonedPage.querySelectorAll('.vmk-investment-included-icon-wrap, .vmk-investment-included-icon').forEach((el) => {
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.minWidth = '40px';
    el.style.minHeight = '40px';
    el.style.overflow = 'visible';
    el.style.flexShrink = '0';
    el.style.display = 'block';
  });
  clonedPage.querySelectorAll('.vmk-warranty-icon-wrap').forEach((el) => {
    el.style.width = '44px';
    el.style.height = '44px';
    el.style.minWidth = '44px';
    el.style.minHeight = '44px';
    el.style.borderRadius = '50%';
    el.style.background = '#179f9f';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.flexShrink = '0';
  });
  clonedPage.querySelectorAll('.vmk-process-icon-box').forEach((el) => {
    el.style.width = '56px';
    el.style.height = '56px';
    el.style.minWidth = '56px';
    el.style.minHeight = '56px';
    el.style.borderRadius = '8px';
    el.style.background = '#eef1f2';
    el.style.display = 'flex';
    el.style.alignItems = 'center';
    el.style.justifyContent = 'center';
    el.style.flexShrink = '0';
  });
  clonedPage.querySelectorAll('.vmk-sustainability-photo-wrap').forEach((el) => {
    el.style.flex = '1 1 auto';
    el.style.minHeight = '180px';
    el.style.overflow = 'hidden';
    el.style.borderRadius = '10px';
  });
  clonedPage.querySelectorAll('.vmk-sustainability-photo').forEach((el) => {
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = 'cover';
    el.style.display = 'block';
  });
  clonedPage.querySelectorAll('.vmk-agreement-signature-box').forEach((el) => {
    el.style.height = '168px';
    el.style.minHeight = '168px';
    el.style.maxHeight = '168px';
    el.style.flexShrink = '0';
  });
  clonedPage.querySelectorAll('.vmk-agreement-qr-image').forEach((el) => {
    el.style.width = '108px';
    el.style.height = '108px';
    el.style.display = 'block';
    el.style.flexShrink = '0';
  });
  clonedPage.querySelectorAll('.vmk-specs-icon-image').forEach((el) => {
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.objectFit = el.classList.contains('vmk-specs-icon-photo') ? 'cover' : 'contain';
    el.style.display = 'block';
    el.style.flexShrink = '0';
  });
  ensureTemplateBadgesInBounds(livePage, clonedPage);
}

/** Match row height from live preview so flex vertical centering is exact in PDF */
function syncFlexCellRowHeights(livePage, clonedPage) {
  const rowConfigs = [
    { rowSelector: '.vmk-price-row', innerSelector: '.vmk-price-cell-inner' },
    { rowSelector: '.vmk-totals-row', innerSelector: '.vmk-totals-cell-inner' },
  ];

  rowConfigs.forEach(({ rowSelector, innerSelector }) => {
    const liveRows = livePage.querySelectorAll(rowSelector);
    const clonedRows = clonedPage.querySelectorAll(rowSelector);

    liveRows.forEach((liveRow, index) => {
      const clonedRow = clonedRows[index];
      if (!clonedRow) return;

      const rowHeight = Math.max(liveRow.getBoundingClientRect().height, 28);
      clonedRow.querySelectorAll(innerSelector).forEach((inner) => {
        inner.style.minHeight = `${Math.round(rowHeight)}px`;
        inner.style.height = `${Math.round(rowHeight)}px`;
      });
    });
  });
}

async function captureQuotePageCanvas(pageEl, options = {}) {
  const { scale = PDF_CAPTURE_SCALE, pageSelector = '[data-pdf-page]' } = options;

  pageEl.scrollIntoView({ block: 'start' });
  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  return html2canvas(pageEl, {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    allowTaint: true,
    width: A4_WIDTH_PX,
    height: A4_HEIGHT_PX,
    windowWidth: A4_WIDTH_PX,
    windowHeight: A4_HEIGHT_PX,
    scrollX: 0,
    scrollY: -window.scrollY,
    letterRendering: true,
    onclone: (clonedDoc, clonedElement) => {
      const clonedPage =
        clonedElement?.matches?.(pageSelector)
          ? clonedElement
          : clonedElement?.querySelector?.(pageSelector) || clonedElement;
      if (clonedPage) {
        prepareClonedPage(pageEl, clonedDoc, clonedPage);
      }
    },
  });
}

function canvasToJpegDataUrl(canvas, quality) {
  return canvas.toDataURL('image/jpeg', quality);
}

function buildPdfFromCanvases(canvases, { marginMm = 0, jpegQuality = PDF_JPEG_QUALITY_START } = {}) {
  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const renderWidth = pdfWidth - marginMm * 2;
  const renderHeight = pdfHeight - marginMm * 2;

  canvases.forEach((canvas, index) => {
    if (index > 0) pdf.addPage();
    pdf.addImage(
      canvasToJpegDataUrl(canvas, jpegQuality),
      'JPEG',
      marginMm,
      marginMm,
      renderWidth,
      renderHeight
    );
  });

  return pdf;
}

function findJpegQualityForSize(canvases, { marginMm, maxBytes }) {
  let quality = PDF_JPEG_QUALITY_START;
  let pdf = buildPdfFromCanvases(canvases, { marginMm, jpegQuality: quality });
  let size = pdf.output('arraybuffer').byteLength;

  while (size > maxBytes && quality - PDF_JPEG_QUALITY_STEP >= PDF_JPEG_QUALITY_MIN) {
    quality = Math.round((quality - PDF_JPEG_QUALITY_STEP) * 100) / 100;
    pdf = buildPdfFromCanvases(canvases, { marginMm, jpegQuality: quality });
    size = pdf.output('arraybuffer').byteLength;
  }

  return { pdf, size, jpegQuality: quality };
}

/** Renumber VMK page footers 1..n for capture; returns restore function */
export function renumberQuotePageFooters(container, pageSelector = '[data-pdf-page]') {
  const restores = [];
  container.querySelectorAll(pageSelector).forEach((pageEl, index) => {
    const pageLabel = String(index + 1).padStart(2, '0');
    const footerMiddle = pageEl.querySelector('.vmk-page-footer span:nth-child(2)');
    if (footerMiddle) {
      restores.push({ el: footerMiddle, text: footerMiddle.textContent });
      footerMiddle.textContent = `- ${index + 1} -`;
    }
    const templateBadge = pageEl.querySelector('.vmk-template-page-badge');
    if (templateBadge) {
      restores.push({ el: templateBadge, text: templateBadge.textContent });
      templateBadge.textContent = pageLabel;
    }
    const coverBadge = pageEl.querySelector('.vmk-cover-badge');
    if (coverBadge) {
      restores.push({ el: coverBadge, text: coverBadge.textContent });
      coverBadge.textContent = pageLabel;
    }
    const proposalBadge = pageEl.querySelector('.vmk-proposal-badge');
    if (proposalBadge) {
      restores.push({ el: proposalBadge, text: proposalBadge.textContent });
      proposalBadge.textContent = pageLabel;
    }
  });
  return () => {
    restores.forEach(({ el, text }) => {
      el.textContent = text;
    });
  };
}

export async function printQuotePages(container, options = {}) {
  const { scale = 2, pageSelector = '[data-pdf-page]' } = options;
  const pages = container.querySelectorAll(pageSelector);
  if (!pages.length) {
    throw new Error('No PDF pages found');
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  const imageSources = [];
  for (let i = 0; i < pages.length; i += 1) {
    const canvas = await captureQuotePageCanvas(pages[i], { scale, pageSelector });
    imageSources.push(canvas.toDataURL('image/png', 1.0));
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', 'Quote print preview');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error('Print frame unavailable');
  }

  const pagesHtml = imageSources
    .map((src) => `<div class="quote-print-page"><img src="${src}" alt="" /></div>`)
    .join('');

  doc.open();
  doc.write(`<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="utf-8" />
<style>
  @page { size: A4 portrait; margin: 0; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    width: 210mm;
  }
  .quote-print-page {
    width: 210mm;
    height: 297mm;
    margin: 0;
    padding: 0;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
  }
  .quote-print-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }
  img {
    width: 210mm;
    height: 297mm;
    display: block;
    object-fit: fill;
  }
</style>
</head>
<body>${pagesHtml}</body>
</html>`);
  doc.close();

  await Promise.all(
    Array.from(doc.images).map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            })
    )
  );

  await new Promise((resolve) => setTimeout(resolve, 150));

  return new Promise((resolve, reject) => {
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      resolve(imageSources.length);
    };

    iframe.contentWindow?.addEventListener('afterprint', cleanup);
    setTimeout(cleanup, 120000);

    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

export async function exportPdfPages(container, filename, options = {}) {
  const {
    scale = PDF_CAPTURE_SCALE,
    marginMm = 0,
    pageSelector = '[data-pdf-page]',
    maxBytes = PDF_MAX_BYTES,
  } = options;
  const pages = container.querySelectorAll(pageSelector);
  if (!pages.length) {
    throw new Error('No PDF pages found');
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  let captureScale = scale;
  let canvases = [];

  const captureAll = async () => {
    canvases = [];
    for (let i = 0; i < pages.length; i += 1) {
      canvases.push(await captureQuotePageCanvas(pages[i], { scale: captureScale, pageSelector }));
    }
  };

  await captureAll();

  let { pdf, size, jpegQuality } = findJpegQualityForSize(canvases, { marginMm, maxBytes });

  // Still too large: re-capture at lower resolution once, then compress again
  if (size > maxBytes && captureScale > 1.25) {
    captureScale = 1.5;
    await captureAll();
    ({ pdf, size, jpegQuality } = findJpegQualityForSize(canvases, { marginMm, maxBytes }));
  }

  pdf.save(filename);
  return pages.length;
}

export async function exportPdfPagesAsDataUri(container, options = {}) {
  const {
    scale = PDF_CAPTURE_SCALE,
    marginMm = 0,
    pageSelector = '[data-pdf-page]',
    maxBytes = PDF_MAX_BYTES,
  } = options;
  const pages = container.querySelectorAll(pageSelector);
  if (!pages.length) {
    throw new Error('No PDF pages found');
  }

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  let captureScale = scale;
  let canvases = [];

  const captureAll = async () => {
    canvases = [];
    for (let i = 0; i < pages.length; i += 1) {
      canvases.push(await captureQuotePageCanvas(pages[i], { scale: captureScale, pageSelector }));
    }
  };

  await captureAll();

  let { pdf } = findJpegQualityForSize(canvases, { marginMm, maxBytes });

  if (pdf.output('arraybuffer').byteLength > maxBytes && captureScale > 1.25) {
    captureScale = 1.5;
    await captureAll();
    ({ pdf } = findJpegQualityForSize(canvases, { marginMm, maxBytes }));
  }

  return pdf.output('datauristring');
}

export { A4_WIDTH_MM, A4_HEIGHT_MM };
